/**
 * Fatura listesi ekranı — **vücut** mertebesi.
 *
 * Şablon (docs/13 bölüm 7): PageHeader → FilterBar → DataTable → Pagination.
 *
 * Tablo **sunucu taraflıdır**: sıralama, sayfalama ve filtreleme veritabanında
 * yapılır (bkz. VeriTablosu). Tüm durum adres çubuğunda tutulur, böylece bir
 * görünüm paylaşılabilir ve F5 sonrası kaybolmaz.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useAdresParametreleri } from '../lib/adres';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { Download, FileSearch, RotateCw } from 'lucide-react';
import { api, type FaturaSatiriOzet } from '../lib/api';
import { Kabuk } from '../components/Kabuk';
import { Kart } from '../components/ui/Kart';
import { Dugme } from '../components/ui/Dugme';
import { HataKutusu } from '../components/Durumlar';
import { DurumRozeti, TipRozeti } from '../components/DurumRozeti';
import { VeriTablosu, KartAlani } from '../components/VeriTablosu';
import { GelismisFiltre, type FiltreDegerleri } from '../components/GelismisFiltre';
import { belgeTipiYaz, paraYaz, tarihYaz, yonYaz } from '../lib/format';
import { sayfaBilgisi } from '../lib/sayfa';

/** Adres çubuğundaki değerleri filtre nesnesine çevirir. */
function adrestenFiltre(p: URLSearchParams): FiltreDegerleri {
  return {
    q: p.get('q') ?? '',
    status: p.get('status') ?? '',
    kind: p.get('kind') ?? '',
    direction: p.get('direction') ?? '',
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
    minAmount: p.get('minAmount') ?? '',
    maxAmount: p.get('maxAmount') ?? '',
    hasPdf: p.get('hasPdf') ?? '',
  };
}

export function FaturaListesi() {
  const { parametreler, setParametreler, adresiGuncelle } = useAdresParametreleri();
  const gecis = useNavigate();

  const filtre = adrestenFiltre(parametreler);
  const sayfaNo = Math.max(1, Number(parametreler.get('page') ?? '1') || 1);
  const sayfaBoyu = Number(parametreler.get('pageSize') ?? '25') || 25;
  const sortBy = parametreler.get('sortBy') ?? 'createdAt';
  const sortDir = (parametreler.get('sortDir') ?? 'desc') as 'asc' | 'desc';

  const sorgu = {
    ...Object.fromEntries(Object.entries(filtre).filter(([, v]) => v !== '')),
    sortBy,
    sortDir,
    page: sayfaNo,
    pageSize: sayfaBoyu,
  };

  const { data, error, isPending, isFetching, refetch } = useQuery({
    queryKey: ['faturalar', sorgu],
    queryFn: () => api.faturalar(sorgu),
    staleTime: 10_000,
    // Sayfa/sıra değişiminde tablo boşalıp zıplamasın — eski veri kalır.
    placeholderData: (onceki) => onceki,
  });

  const kolonlar = useMemo<ColumnDef<FaturaSatiriOzet, unknown>[]>(
    () => [
      {
        id: 'invoiceNumber',
        accessorKey: 'invoiceNumber',
        header: 'Fatura no',
        enableSorting: true,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="rakam text-sm font-medium text-[var(--accent)]">
              {row.original.invoiceNumber ?? '— numara yok —'}
            </span>
            {row.original.shopifyOrderNumber && (
              <span className="text-xs text-[var(--ink-faint)]">
                Sipariş {row.original.shopifyOrderNumber}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'buyerTitle',
        accessorKey: 'buyerTitle',
        header: 'Alıcı',
        enableSorting: true,
        cell: ({ row }) => (
          // Uzun unvan tabloyu yana taşırıyordu (ölçüldü: 1440px'te Durum sütunu kesildi) → kırp.
          <div className="flex min-w-0 max-w-[32ch] flex-col" title={row.original.buyerTitle ?? undefined}>
            <span className="truncate text-sm text-[var(--ink)]">{row.original.buyerTitle ?? '—'}</span>
            {row.original.buyerIdentifier && (
              <span className="rakam text-xs text-[var(--ink-faint)]">{row.original.buyerIdentifier}</span>
            )}
          </div>
        ),
      },
      {
        id: 'kind',
        accessorKey: 'kind',
        header: 'Tip',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            <TipRozeti etiket={belgeTipiYaz(row.original.kind)} />
            {row.original.direction === 'CREDIT_NOTE' && (
              <TipRozeti etiket={yonYaz(row.original.direction)} />
            )}
          </div>
        ),
      },
      {
        id: 'issueDate',
        accessorKey: 'issueDate',
        header: 'Tarih',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="rakam text-sm whitespace-nowrap text-[var(--ink-muted)]">
            {tarihYaz(row.original.issueDate)}
          </span>
        ),
      },
      {
        id: 'payableAmount',
        accessorKey: 'payableAmount',
        header: 'Tutar',
        enableSorting: true,
        // Sayı sütunu sağa yaslı + tabular-nums (docs/13 bölüm 5).
        meta: { sagaYasli: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            <span className="rakam text-sm font-medium text-[var(--ink)]">
              {paraYaz(row.original.payableAmount)}
            </span>
            <span className="ml-1 text-xs text-[var(--ink-faint)]">
              {row.original.currency === 'TRY' ? '₺' : row.original.currency}
            </span>
          </span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Durum',
        enableSorting: true,
        cell: ({ row }) => <DurumRozeti etiket={row.original.statusLabel} ton={row.original.statusTone} />,
      },
      {
        id: 'belge',
        header: 'Belge',
        enableSorting: false,
        meta: { sagaYasli: true },
        cell: ({ row }) =>
          row.original.hasPdf ? (
            <a
              href={api.belgeAdresi(row.original.uniqueIntegrationCode, 'pdf')}
              target="_blank"
              rel="noreferrer"
              // Satır tıklaması detaya götürüyor; PDF bağlantısı onu tetiklemesin.
              onClick={(olay) => olay.stopPropagation()}
              title="Faturanın PDF kopyasını aç"
              className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1.5 text-xs font-medium text-[var(--accent)] hover:bg-[var(--accent-soft)]"
            >
              <Download size={14} aria-hidden="true" />
              PDF
            </a>
          ) : (
            <span
              className="text-xs text-[var(--ink-faint)]"
              title="Veriban belgeyi hazırlayınca sistem otomatik indirir"
            >
              hazır değil
            </span>
          ),
      },
    ],
    []
  );

  const siralama: SortingState = [{ id: sortBy, desc: sortDir === 'desc' }];

  return (
    <Kabuk
      baslik="Faturalar"
      altBaslik={data ? `${data.total} kayıt` : 'Kesilen e-Arşiv ve e-Fatura belgeleri'}
    >
      <GelismisFiltre
        deger={filtre}
        onDegistir={(yama) => adresiGuncelle(yama)}
        onTemizle={() => setParametreler(new URLSearchParams())}
        eylemler={
          <Dugme
            gorunum="ikincil"
            ikon={<RotateCw size={15} />}
            bekliyor={isFetching && !isPending}
            onClick={() => void refetch()}
            aria-label="Listeyi yenile"
          >
            <span className="hidden @sm:inline">Yenile</span>
          </Dugme>
        }
      />

      {error !== null && <HataKutusu hata={error} tekrarDene={() => void refetch()} />}

      <Kart>
        <VeriTablosu
          veri={data?.items ?? []}
          kolonlar={kolonlar}
          sayfa={sayfaBilgisi(data, sayfaNo, sayfaBoyu)}
          siralama={siralama}
          yukleniyor={isPending}
          tazeleniyor={isFetching && !isPending}
          satirAnahtari={(satir) => satir.id}
          onSiralama={(yeni) => {
            const ilk = yeni[0];
            // Üçüncü tıklamada sıralama kalkarsa varsayılana dönülür.
            adresiGuncelle(
              ilk
                ? { sortBy: ilk.id, sortDir: ilk.desc ? 'desc' : 'asc' }
                : { sortBy: undefined, sortDir: undefined },
              false
            );
          }}
          onSayfa={(yeniSayfa) => {
            adresiGuncelle({ page: yeniSayfa > 1 ? yeniSayfa : undefined }, false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onSayfaBoyu={(boy) => adresiGuncelle({ pageSize: boy === 25 ? undefined : boy })}
          onSatirTikla={(satir) =>
            void gecis(`/faturalar/${encodeURIComponent(satir.uniqueIntegrationCode)}`)
          }
          kartGoster={(satir) => (
            <div className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <span className="rakam text-sm font-semibold text-[var(--accent)]">
                  {satir.invoiceNumber ?? '— numara yok —'}
                </span>
                <DurumRozeti etiket={satir.statusLabel} ton={satir.statusTone} />
              </div>
              <span className="truncate text-sm text-[var(--ink)]">{satir.buyerTitle ?? '—'}</span>
              <div className="flex flex-col gap-0.5 border-t border-[var(--line)] pt-2">
                <KartAlani etiket="Tutar">
                  <span className="rakam font-medium">
                    {paraYaz(satir.payableAmount)} {satir.currency === 'TRY' ? '₺' : satir.currency}
                  </span>
                </KartAlani>
                <KartAlani etiket="Tarih">
                  <span className="rakam">{tarihYaz(satir.issueDate)}</span>
                </KartAlani>
                <KartAlani etiket="Belge tipi">{belgeTipiYaz(satir.kind)}</KartAlani>
                <KartAlani etiket="PDF">
                  {satir.hasPdf ? (
                    <span className="text-[var(--state-ok)]">hazır</span>
                  ) : (
                    <span className="text-[var(--ink-faint)]">henüz yok</span>
                  )}
                </KartAlani>
              </div>
            </div>
          )}
          bosBaslik="Bu görünüme uyan fatura yok"
          bosAciklama="Filtreleri gevşetip tekrar deneyin. Hiç fatura kesilmediyse, Shopify’da bir sipariş ödendiğinde otomatik kesilecek ve burada görünecek."
          bosEylem={
            <Dugme
              gorunum="ikincil"
              ikon={<FileSearch size={15} />}
              onClick={() => setParametreler(new URLSearchParams())}
            >
              Filtreleri temizle
            </Dugme>
          }
        />
      </Kart>
    </Kabuk>
  );
}
